// src/ComponentesGlobales/ScannerUnico.jsx
import { useEffect, useRef, useState, useMemo } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import "./qrScanner.css";

export default function ScannerUnico({
  onScan,
  isActive,
  onClose,
  closeOnRead = true,          // si true, cierra al leer
  beep = true,                 // activa beep al leer
  vibrate = true,              // vibración (si está disponible)
}) {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);
  const mediaTrackRef = useRef(null);
  const readGuardRef = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [error, setError] = useState("");
  const [torchOn, setTorchOn] = useState(false);

  // cámaras
  const [devices, setDevices] = useState([]);           // [{deviceId,label}]
  const [selectedId, setSelectedId] = useState(null);

  const isSecureContext =
    location.protocol === "https:" ||
    location.hostname === "localhost" ||
    location.hostname === "127.0.0.1";

  // ------------------- Utils -------------------
  function playBeep() {
    if (!beep) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = 880; // A5
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
      o.connect(g);
      g.connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.13);
    } catch {}
  }

  function haptic() {
    if (vibrate && navigator.vibrate) navigator.vibrate(60);
  }

  function pickBackCamera(videoInputs) {
    if (!videoInputs?.length) return null;
    const back = videoInputs.find(d => /back|rear|environment/i.test(d.label)) || videoInputs[0];
    return back.deviceId || null;
  }

  async function stop() {
    try {
      try { controlsRef.current?.stop(); } catch {}
      try { readerRef.current?.reset(); } catch {}
      controlsRef.current = null;
      readerRef.current = null;

      const track = mediaTrackRef.current;
      if (track) {
        try { await track.applyConstraints({ advanced: [{ torch: false }] }); } catch {}
        try { track.stop(); } catch {}
      }
      mediaTrackRef.current = null;

      const v = videoRef.current;
      if (v) {
        try {
          if (!v.paused) v.pause();
          const s = v.srcObject;
          if (s?.getTracks) s.getTracks().forEach(t => t.stop());
          v.srcObject = null;
          v.removeAttribute("srcObject");
          v.removeAttribute("src");
          v.load();
        } catch {}
      }
      readGuardRef.current = false;
      setTorchOn(false);
      setVideoReady(false);
      setIsLoading(false);
    } catch (e) {
      console.warn("stop() error:", e);
    }
  }

  async function toggleTorch() {
    const track = mediaTrackRef.current;
    if (!track) return;
    const caps = track.getCapabilities?.();
    if (!caps?.torch) return;
    const shouldOn = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: shouldOn }] });
      setTorchOn(shouldOn);
    } catch (e) {
      console.warn("No se pudo cambiar la linterna:", e);
    }
  }

  // ------------------- START / RESTART con deviceId -------------------
  async function start(deviceId) {
    setError("");
    setIsLoading(true);
    setVideoReady(false);

    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.QR_CODE,
      BarcodeFormat.CODE_128,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.CODE_39,
      BarcodeFormat.ITF,
    ]);

    const reader = new BrowserMultiFormatReader(hints, 300);
    readerRef.current = reader;

    reader.decodeFromVideoDevice(
      deviceId ?? null,
      videoRef.current,
      async (result, err, controls) => {
        if (controls && !controlsRef.current) {
          controlsRef.current = controls;

          const stream = videoRef.current?.srcObject;
          const track = stream?.getVideoTracks?.()[0];
          if (track) mediaTrackRef.current = track;

          const video = videoRef.current;
          if (video) {
            const onReady = () => {
              setVideoReady(true);
              setIsLoading(false);
              video.removeEventListener("loadedmetadata", onReady);
              video.removeEventListener("canplay", onReady);
            };
            video.addEventListener("loadedmetadata", onReady);
            video.addEventListener("canplay", onReady);

            // fallback por si canplay no dispara
            setTimeout(() => {
              if (!videoReady && video.videoWidth > 0) {
                setVideoReady(true);
                setIsLoading(false);
              }
            }, 1500);
          }
        }

        if (result && !readGuardRef.current) {
          readGuardRef.current = true;
          const text = result.getText?.() ?? "";
          if (text.trim()) {
            playBeep();
            haptic();
            onScan?.(text.trim());
            if (closeOnRead) {
              await stop();
              onClose?.();
            } else {
              // si querés seguir escaneando, liberá el guard después de un ratito
              setTimeout(() => (readGuardRef.current = false), 350);
            }
          } else {
            readGuardRef.current = false;
          }
        }
      }
    );
  }

  // ------------------- Efectos -------------------
  useEffect(() => {
    (async () => {
      if (!isActive) { await stop(); return; }
      if (!isSecureContext) { setError("La cámara requiere HTTPS o localhost"); return; }
      if (!navigator.mediaDevices?.getUserMedia) { setError("Tu navegador no soporta acceso a la cámara"); return; }

      // Pedimos permisos y listamos cámaras
      try {
        // pedir un stream para obtener labels de dispositivos
        const tmp = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        tmp.getTracks().forEach(t => t.stop());
      } catch {}

      const all = await navigator.mediaDevices.enumerateDevices();
      const vids = all.filter(d => d.kind === "videoinput");
      setDevices(vids);

      // elegir por defecto la trasera
      const def = pickBackCamera(vids);
      setSelectedId(def);
    })();

    return () => { stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // cuando cambia la cámara seleccionada -> reiniciar
  useEffect(() => {
    (async () => {
      if (!isActive) return;
      if (!selectedId && devices.length === 0) return;
      await stop();
      await start(selectedId);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // ------------------- UI -------------------
  if (!isActive) return null;

  const multipleCams = devices.length > 1;

  // botón toggle (frontal<->trasera)
  function toggleCamera() {
    if (!multipleCams) return;
    if (!selectedId) return;
    const idx = devices.findIndex(d => d.deviceId === selectedId);
    const next = devices[(idx + 1) % devices.length];
    setSelectedId(next?.deviceId ?? selectedId);
  }

  return (
    <div className="qr-scanner-overlay">
      <div className="qr-scanner-modal">
        <div className="qr-scanner-header">
          <h3>Escanear Código</h3>
          <button
            onClick={async () => { await stop(); onClose?.(); }}
            className="qr-close-btn"
          >
            ✕
          </button>
        </div>

        <div className="qr-scanner-content">
          {/* Selector de cámara */}
          {devices.length > 0 && (
            <div className="cambiar-camara" style={{ display:'flex', gap:8, alignItems:'center', marginBottom:8 }}>
              {/* <select
                value={selectedId ?? ""}
                onChange={(e) => setSelectedId(e.target.value || null)}
                style={{ flex:1, padding:'8px', borderRadius:8 }}
              >
                {devices.map((d, i) => {
                  const label = d.label || (i === 0 ? "Cámara 1" : `Cámara ${i+1}`);
                  return <option key={d.deviceId} value={d.deviceId}>{label}</option>;
                })}
              </select> */}
              {multipleCams && (
                <button onClick={toggleCamera} className="qr-cancel-btn" style={{ whiteSpace:'nowrap', background:'#28a745' }}>
                  Cambiar cámara
                </button>
              )}
            </div>
          )}

          <div className="qr-scanner-video-container" style={{ position:'relative', width:'100%', height:300, overflow:'hidden' }}>
            <video
              ref={videoRef}
              className="qr-scanner-video"
              playsInline
              muted
              autoPlay
              style={{ width:'100%', height:'100%', objectFit:'cover', background:'#000', opacity: videoReady ? 1 : 0 }}
            />
            {(isLoading || !videoReady) && (
              <div className="video-placeholder" style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,.6)', color:'#fff', zIndex:10 }}>
                <div style={{ textAlign:'center' }}>
                  <div className="loading-spinner" />
                  <p>Iniciando cámara...</p>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="qr-scanner-error" style={{ marginTop: 8 }}>
              <p>⚠️ {error}</p>
            </div>
          )}

          <div style={{ marginTop:12, display:'flex', gap:8, justifyContent:'center', flexWrap:'wrap' }}>
            <button
              onClick={async () => { await stop(); onClose?.(); }}
              className="qr-cancel-btn" style={{ background: '#6c757d' }}
            >
              Cancelar
            </button>
            <button onClick={toggleTorch} className="qr-cancel-btn" style={{ background: torchOn ? "#28a745" : "#6c757d" }}>
              {torchOn ? "Apagar linterna" : "Prender linterna"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
